'use client';

/**
 * TurnstileWidget — Cloudflare Turnstile bot-check widget.
 *
 * Renders nothing (no DOM, no script load) when
 * NEXT_PUBLIC_TURNSTILE_SITEKEY is unset — the form just submits
 * without a token, and the backend (FEATURE_TURNSTILE=0 in dev)
 * skips verification.
 *
 * When sitekey IS set, the widget loads Cloudflare's challenge
 * script, calls our onToken callback as soon as the token is
 * available, and re-issues if the form is re-submitted (single-use
 * tokens are good for ~5 min).
 */

import { useEffect, useRef } from 'react';

const SITEKEY = process.env.NEXT_PUBLIC_TURNSTILE_SITEKEY ?? '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        cfg: {
          sitekey: string;
          action?: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      reset: (widgetId: string) => void;
    };
  }
}

export function TurnstileWidget({
  onToken,
  action,
  theme = 'auto',
}: {
  onToken: (token: string | null) => void;
  /** Per-form action label so tokens minted on /waitlist can't be replayed against /escalations. */
  action: string;
  theme?: 'light' | 'dark' | 'auto';
}): React.ReactElement | null {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip entirely when sitekey isn't configured (dev / pre-Cloudflare).
    // The form will submit without cfToken; the backend (with
    // FEATURE_TURNSTILE=0) silently allows.
    if (!SITEKEY) return;
    let cancelled = false;

    function render(): void {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITEKEY,
          action,
          theme,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      } catch (err) {
        // Cloudflare can throw if the same div was double-rendered (e.g. in
        // dev hot-reload). Swallow; the form will still submit and the
        // backend rate-limit + magic-link auth catches abuse upstream.
        console.warn('[turnstile] render error:', (err as Error).message);
      }
    }

    if (window.turnstile) {
      render();
    } else {
      // Inject the script once per page lifetime.
      const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
      if (existing) {
        existing.addEventListener('load', render, { once: true });
      } else {
        const s = document.createElement('script');
        s.src = SCRIPT_URL;
        s.async = true;
        s.defer = true;
        s.onload = render;
        document.head.appendChild(s);
      }
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.reset(widgetIdRef.current); } catch { /* ignore */ }
      }
    };
  }, [action, theme, onToken]);

  // Don't render anything visible when sitekey isn't configured —
  // both visually clean for dev AND avoids the empty <div> being
  // mistaken for "the widget failed to render" by users.
  if (!SITEKEY) return null;
  return <div ref={containerRef} className="my-3" />;
}
